
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CalendarDays, CheckCircle, Award, TrendingUp } from 'lucide-react';
import { UserProgress } from '@/types/user';
import { Progress } from '@/components/ui/progress';
import { useIsMobile } from '@/hooks/use-mobile';

interface ProgressSummaryCardsProps {
  userProgress: UserProgress;
}

const ProgressSummaryCards: React.FC<ProgressSummaryCardsProps> = ({ userProgress }) => {
  const isMobile = useIsMobile();
  // Calculate progress percentage
  const progressPercentage = Math.min((userProgress.currentDay / 30) * 100, 100);
  
  // Calculate lessons completed
  const lessonsCompleted = userProgress.completedDays?.length || 0;
  
  // Calculate streaks (fictional data for now)
  const currentStreak = userProgress.streak || Math.min(lessonsCompleted, 7);
  
  // Calculate user level based on points
  const userPoints = userProgress.points || lessonsCompleted * 10;
  const userLevel = Math.max(1, Math.floor(userPoints / 100) + 1);
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
      <Card className="bg-white hover:shadow-md transition-all">
        <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-hormony-charcoal/60">Current Day</p>
              <h3 className="text-xl md:text-3xl font-semibold text-hormony-charcoal">{userProgress.currentDay} <span className="text-sm text-hormony-charcoal/50">/ 30</span></h3>
            </div>
            <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-hormony-coral/10 flex items-center justify-center text-hormony-coral">
              <CalendarDays size={isMobile ? 20 : 24} />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-white hover:shadow-md transition-all">
        <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-hormony-charcoal/60">Lessons Done</p>
              <h3 className="text-xl md:text-3xl font-semibold text-hormony-charcoal">{lessonsCompleted}</h3>
            </div>
            <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-hormony-progress/10 flex items-center justify-center text-hormony-progress">
              <CheckCircle size={isMobile ? 20 : 24} />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-white hover:shadow-md transition-all">
        <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm text-hormony-charcoal/60">Day Streak</p>
              <h3 className="text-xl md:text-3xl font-semibold text-hormony-charcoal">{currentStreak}</h3>
            </div>
            <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-hormony-coral/10 flex items-center justify-center text-hormony-coral">
              <TrendingUp size={isMobile ? 20 : 24} />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-white hover:shadow-md transition-all">
        <CardContent className={`${isMobile ? 'p-3' : 'p-6'}`}>
          <div className="flex flex-col justify-between h-full">
            <div className="flex justify-between items-center">
              <p className="text-xs md:text-sm text-hormony-charcoal/60">Level {userLevel}</p>
              <div className="h-6 w-6 md:h-8 md:w-8 rounded-full bg-hormony-coral/10 flex items-center justify-center text-hormony-coral">
                <Award size={isMobile ? 14 : 18} />
              </div>
            </div>
            
            <div className="mt-1 md:mt-2">
              <div className="flex justify-between items-center text-xs mb-1">
                <span>{userPoints} pts</span>
                <span>{(userLevel) * 100} pts</span>
              </div>
              <Progress 
                value={userPoints % 100} 
                className="h-1.5 md:h-2 bg-hormony-beige/30" 
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProgressSummaryCards;
